import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { INSTRUMENT_TYPE_COLORS, INSTRUMENT_TYPE_LEGEND_ORDER } from '../utils/constants'
import { buildDayInstrumentBarTooltip } from '../utils/studyTimelineScheduleDisplay'

const OverviewView = ({
  days,
  scheduledItems,
  cycleRanges,
  selectedDay,
  onSelectDay,
  visitIdToName = {},
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
}) => {
  const daysWithEvents = useMemo(() => {
    const set = new Set()
    scheduledItems.forEach(item => {
      if (item && typeof item.studyDay === 'number') {
        set.add(item.studyDay)
      }
    })
    return set
  }, [scheduledItems])

  // Separate visits and instruments by day
  const dayToItems = useMemo(() => {
    const map = new Map()
    scheduledItems.forEach(item => {
      if (item && typeof item.studyDay === 'number') {
        if (!map.has(item.studyDay)) {
          map.set(item.studyDay, { visits: [], instruments: [] })
        }
        if (item.type === 'visit') {
          map.get(item.studyDay).visits.push(item)
        } else if (item.type === 'instrument') {
          map.get(item.studyDay).instruments.push(item)
        }
      }
    })
    return map
  }, [scheduledItems])

  // Create a map of day to cycle info for active cycles
  const daysInCycles = useMemo(() => {
    const map = new Map()
    cycleRanges.forEach(range => {
      for (let day = range.startDay; day <= range.endDay; day++) {
        if (!map.has(day)) {
          map.set(day, [])
        }
        map.get(day).push(range)
      }
    })
    return map
  }, [cycleRanges])

  // Get cycle labels for specific days (start/end)
  const cycleLabels = useMemo(() => {
    const labels = new Map()
    scheduledItems.forEach(item => {
      if (item.type === 'cycle' && item.studyDay) {
        if (!labels.has(item.studyDay)) {
          labels.set(item.studyDay, [])
        }
        labels.get(item.studyDay).push(item.label)
      }
    })
    return labels
  }, [scheduledItems])

  // Calculate which cycle range each day belongs to (for continuous bar styling)
  // Use the first cycle range found for each day (in case of overlaps)
  const dayToCycleRange = useMemo(() => {
    const map = new Map()
    cycleRanges.forEach(range => {
      for (let day = range.startDay; day <= range.endDay; day++) {
        // Only set if not already set (use first cycle found)
        if (!map.has(day)) {
          map.set(day, range)
        }
      }
    })
    return map
  }, [cycleRanges])

  // Determine if a day is at the start/end of a cycle for styling
  const getCyclePosition = day => {
    const range = dayToCycleRange.get(day)
    if (!range) return null
    const isStart = day === range.startDay
    const isEnd = day === range.endDay
    const isMiddle = !isStart && !isEnd
    return { range, isStart, isEnd, isMiddle }
  }

  return (
    <div className='study-timeline-overview'>
      <div className='study-timeline-grid'>
        {days.map(day => {
          const hasEvent = daysWithEvents.has(day)
          const isInCycle = daysInCycles.has(day)
          const isSelected = day === selectedDay
          const dayLabels = cycleLabels.get(day) || []
          const startLabels = dayLabels.filter(label => label.startsWith('Start:'))
          const endLabels = dayLabels.filter(label => label.startsWith('End:'))
          const hasMultipleCycleLabels = startLabels.length + endLabels.length > 1
          const cyclePos = getCyclePosition(day)

          const dayItems = dayToItems.get(day)
          const hasVisit = dayItems?.visits.length > 0
          const hasInstrument = dayItems?.instruments.length > 0

          const className = [
            'study-timeline-grid-cell',
            hasEvent ? 'has-event' : '',
            isInCycle ? 'in-cycle' : '',
            cyclePos?.isStart ? 'cycle-start' : '',
            cyclePos?.isEnd ? 'cycle-end' : '',
            cyclePos?.isMiddle ? 'cycle-middle' : '',
            isSelected ? 'selected' : '',
            hasVisit ? 'has-visit' : '',
            hasInstrument ? 'has-instrument' : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <button
              key={day}
              type='button'
              className={className}
              onClick={() => onSelectDay(day)}
            >
              {isInCycle && (
                <div
                  className={`study-timeline-cycle-bar ${cyclePos?.isStart ? 'cycle-bar-start' : ''
                    } ${cyclePos?.isEnd ? 'cycle-bar-end' : ''} ${cyclePos?.isMiddle ? 'cycle-bar-middle' : ''
                    }`}
                />
              )}
              {(startLabels.length > 0 || endLabels.length > 0) && (
                <div
                  className={`study-timeline-cycle-labels${hasMultipleCycleLabels ? ' study-timeline-cycle-labels-stacked' : ''}`}
                >
                  {endLabels.map((label, idx) => (
                    <div
                      key={`end-${idx}`}
                      className='study-timeline-cycle-label study-timeline-cycle-label-end'
                      title={label}
                    >
                      {label}
                    </div>
                  ))}
                  {startLabels.map((label, idx) => (
                    <div
                      key={`start-${idx}`}
                      className='study-timeline-cycle-label study-timeline-cycle-label-start'
                      title={label}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
              <div className='study-timeline-grid-day-number'>{day}</div>
              {(() => {
                const dayItems = dayToItems.get(day)
                if (!dayItems) return null
                const firstVisitLabel = dayItems.visits.length > 0 ? (dayItems.visits[0].label || '') : ''
                return (
                  <>
                    {dayItems.visits.length > 0 && (
                      <div className='study-timeline-grid-bar study-timeline-visit-bar'>
                        <span
                          className='study-timeline-visit-bar-label'
                          title={firstVisitLabel}
                        >
                          {firstVisitLabel}
                        </span>
                      </div>
                    )}
                    {dayItems.instruments.length > 0 && (() => {
                      const instrumentTooltip = buildDayInstrumentBarTooltip(dayItems.instruments, {
                        visitIdToName,
                        useAbsoluteStartDate,
                        absoluteStartDate,
                      })
                      const typesPresent = [...new Set(
                        dayItems.instruments
                          .map(instr => (instr.instrumentData?.type || '').toUpperCase())
                          .filter(Boolean)
                      )]
                      const orderedTypes = INSTRUMENT_TYPE_LEGEND_ORDER
                        .map(({ key }) => key)
                        .filter(key => typesPresent.includes(key))
                      if (orderedTypes.length === 0) {
                        return (
                          <div
                            className='study-timeline-grid-bar study-timeline-instrument-bar'
                            title={instrumentTooltip || undefined}
                          />
                        )
                      }
                      return (
                        <div
                          className='study-timeline-grid-bar study-timeline-instrument-bar study-timeline-instrument-bar-segments'
                          title={instrumentTooltip || undefined}>
                          {orderedTypes.map(typeKey => (
                            <div
                              key={typeKey}
                              className={`study-timeline-instrument-segment instrument-type-${typeKey.toLowerCase()}`}
                              style={{
                                backgroundColor: INSTRUMENT_TYPE_COLORS[typeKey]?.border || '#bfe0f9',
                              }}
                            />
                          ))}
                        </div>
                      )
                    })()}
                  </>
                )
              })()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

OverviewView.propTypes = {
  days: PropTypes.arrayOf(PropTypes.number).isRequired,
  scheduledItems: PropTypes.arrayOf(
    PropTypes.shape({
      studyDay: PropTypes.number,
      type: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  cycleRanges: PropTypes.arrayOf(
    PropTypes.shape({
      cycleId: PropTypes.string,
      cycleName: PropTypes.string,
      startDay: PropTypes.number,
      endDay: PropTypes.number,
    }),
  ),
  selectedDay: PropTypes.number,
  onSelectDay: PropTypes.func.isRequired,
  visitIdToName: PropTypes.object,
  useAbsoluteStartDate: PropTypes.bool,
  absoluteStartDate: PropTypes.string,
}

export default OverviewView

